import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import CollateralCard, { getAvgReadPct } from './CollateralCard';
import PlaybooksPanel from './PlaybooksPanel';
import type { CollateralData } from '../../api/client';

type LibTab = 'collaterals' | 'playbooks';

const TYPE_CHIPS = [
  { value: 'pdf',   label: 'PDF',          icon: 'ti-file-type-pdf' },
  { value: 'ppt',   label: 'Presentation', icon: 'ti-presentation' },
  { value: 'video', label: 'Video',         icon: 'ti-video' },
  { value: 'url',   label: 'URL / Link',   icon: 'ti-link' },
];

const OVERLAY: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(15,27,45,.6)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' };
const CARD: React.CSSProperties    = { background: 'white', borderRadius: '18px', width: '100%', maxWidth: '520px', boxShadow: '0 32px 80px rgba(0,0,0,.3)', overflow: 'hidden' };
const HDR: React.CSSProperties     = { background: '#0F1B2D', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: '12px' };
const BODY: React.CSSProperties    = { padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' };
const LBL: React.CSSProperties     = { display: 'block', fontSize: '13px', fontWeight: '600', color: '#1A202C', marginBottom: '6px' };
const fieldInp = (err: boolean): React.CSSProperties => ({
  width: '100%', padding: '11px 14px', borderRadius: '10px', fontFamily: 'inherit', fontSize: '14px',
  border: `1.5px solid ${err ? '#FC8181' : '#E2E8F0'}`, outline: 'none', boxSizing: 'border-box',
  background: err ? '#FFF5F5' : 'white', color: '#1A202C',
});
const ERR: React.CSSProperties = { fontSize: '11px', color: '#E53E3E', marginTop: '4px' };

function UploadModal({ onClose, currentUser }: { onClose: () => void; currentUser: { name: string; ini: string } }) {
  const { setColls, toast } = useApp();
  const [form, setForm] = useState({ name: '', type: 'pdf', url: '', size: '', tags: '', pages: '1', note: '' });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (k: keyof typeof form, v: string) => {
    setForm(prev => ({ ...prev, [k]: v }));
    setErrors(prev => ({ ...prev, [k]: '' }));
  };

  const submit = async () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (form.type === 'url' && !form.url.trim()) e.url = 'URL is required';
    if (form.type === 'url' && form.url && !/^https?:\/\//i.test(form.url)) e.url = 'Must start with https://';
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const now = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      const newColl: CollateralData = {
        _id: `local-${Date.now()}`,
        name: form.name.trim(), type: form.type,
        size: form.type === 'url' ? 'URL' : (form.size.trim() || '—'),
        pages: parseInt(form.pages) || 1,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        sharedBy: currentUser.name, sharedByIni: currentUser.ini, sharedDate: now,
        recipients: [],
        pdata: [{ t: form.name.trim(), s: form.note.trim(), b: '' }],
        url: form.type === 'url' ? form.url.trim() : undefined,
      };
      setColls(prev => [newColl, ...prev]);
      toast('Collateral added successfully', 'success');
      onClose();
    } catch {
      toast('Failed to add collateral', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={OVERLAY} onClick={onClose}>
      <div style={CARD} onClick={e => e.stopPropagation()}>
        <div style={HDR}>
          <i className="ti ti-upload" style={{ fontSize: '20px', color: '#C8973A' }} />
          <span style={{ fontSize: '17px', fontWeight: '700', color: 'white', flex: 1 }}>Add collateral</span>
          <button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,.15)', border: 'none', borderRadius: '8px', color: 'white', width: '30px', height: '30px', cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        <div style={BODY}>
          {/* Name */}
          <div>
            <label style={LBL}>Name *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Q3 Product Deck.pdf" style={fieldInp(!!errors.name)} />
            {errors.name && <div style={ERR}>{errors.name}</div>}
          </div>

          {/* Type chips */}
          <div>
            <label style={LBL}>Type</label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {TYPE_CHIPS.map(t => {
                const active = form.type === t.value;
                return (
                  <button key={t.value} type="button" onClick={() => set('type', t.value)}
                    style={{ padding: '7px 16px', borderRadius: '999px', border: `1.5px solid ${active ? '#C8973A' : '#E2E8F0'}`, background: 'white', fontFamily: 'inherit', fontSize: '13px', fontWeight: active ? '600' : '400', color: active ? '#C8973A' : '#4A5568', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <i className={`ti ${t.icon}`} style={{ fontSize: '13px' }} /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* URL or Size+Pages */}
          {form.type === 'url' ? (
            <div>
              <label style={LBL}>URL *</label>
              <input value={form.url} onChange={e => set('url', e.target.value)} placeholder="https://..." style={fieldInp(!!errors.url)} />
              {errors.url && <div style={ERR}>{errors.url}</div>}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div>
                <label style={LBL}>File size</label>
                <input value={form.size} onChange={e => set('size', e.target.value)} placeholder="e.g. 4.2 MB" style={fieldInp(false)} />
              </div>
              <div>
                <label style={LBL}>Pages</label>
                <input type="number" min="1" value={form.pages} onChange={e => set('pages', e.target.value)} placeholder="1" style={fieldInp(false)} />
              </div>
            </div>
          )}

          {/* Tags */}
          <div>
            <label style={LBL}>Tags <span style={{ fontWeight: '400', color: '#A0AEC0' }}>(comma separated)</span></label>
            <input value={form.tags} onChange={e => set('tags', e.target.value)} placeholder="e.g. Q3, Pricing, Demo" style={fieldInp(false)} />
          </div>

          {/* Note */}
          <div>
            <label style={LBL}>Description <span style={{ fontWeight: '400', color: '#A0AEC0' }}>(optional)</span></label>
            <textarea value={form.note} onChange={e => set('note', e.target.value)}
              placeholder="e.g. Updated pricing sheet for Q3 enterprise prospects"
              rows={2} style={{ ...fieldInp(false), resize: 'vertical', minHeight: '64px' }} />
          </div>
        </div>

        <div style={{ padding: '0 24px 24px' }}>
          <button type="button" onClick={submit} disabled={saving}
            style={{ width: '100%', padding: '14px', borderRadius: '12px', background: saving ? '#718096' : '#0F1B2D', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '15px', fontWeight: '700', cursor: saving ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            {saving
              ? <><i className="ti ti-loader-2" style={{ animation: 'spin 1s linear infinite' }} /> Saving…</>
              : <><i className="ti ti-upload" /> Add to library</>}
          </button>
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

export default function LibraryScreen() {
  const { colls, playbooks, toast, libraryTab: tab, setLibraryTab: setTab, currentUser } = useApp();
  const [showUpload, setShowUpload] = useState(false);
  const { isMobile, rp } = useBreakpoint();
  const [sel, setSel] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [readFilter, setReadFilter] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const [senderFilter, setSenderFilter] = useState('');
  const [previewModal, setPreviewModal] = useState<{ coll: CollateralData; page: number } | null>(null);
  const px = rp({ mobile: '14px', tablet: '18px', desktop: '24px' });

  const filtered = useMemo(() => {
    let items = [...colls];
    if (typeFilter !== 'all') items = items.filter(c => c.type === typeFilter);
    if (senderFilter) items = items.filter(c => c.sharedBy === senderFilter);
    if (search) items = items.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.tags.some(t => t.toLowerCase().includes(search.toLowerCase())));
    if (readFilter) {
      items = items.filter(c => {
        const avg = getAvgReadPct(c);
        if (readFilter === 'unread') return avg === 0;
        if (readFilter === 'partial') return avg > 0 && avg < 100;
        if (readFilter === 'read') return avg === 100;
        return true;
      });
    }
    if (sortBy === 'name') items.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortBy === 'read-desc') items.sort((a, b) => getAvgReadPct(b) - getAvgReadPct(a));
    return items;
  }, [colls, typeFilter, senderFilter, search, readFilter, sortBy]);

  const toggleCard = (id: number | string) => {
    const sid = String(id);
    setSel(prev => prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]);
  };

  const selectStyle: React.CSSProperties = {
    padding: '7px 24px 7px 9px', borderRadius: '8px', border: '1.5px solid var(--border2)',
    fontFamily: 'inherit', fontSize: '12px', background: 'white', cursor: 'pointer',
    outline: 'none', appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='%23718096' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 7px center',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: `14px ${px} 0`, display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: rp({ mobile: '16px', desktop: '18px' }), fontWeight: '700', color: 'var(--navy)' }}>Collateral Library</div>
          {!isMobile && <div style={{ fontSize: '12px', color: 'var(--text3)' }}>Select collaterals · create a Sales Playbook</div>}
        </div>
        <button onClick={() => setShowUpload(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '7px 12px', borderRadius: '8px', border: '1.5px solid var(--border2)', background: 'white', color: 'var(--text2)', fontFamily: 'inherit', fontSize: '12px', fontWeight: '500', cursor: 'pointer', flexShrink: 0 }}>
          <i className="ti ti-upload" />{!isMobile && ' Upload'}
        </button>
      </div>

      {/* Selection bar */}
      {sel.length > 0 && (
        <div style={{ background: 'var(--navy)', color: 'white', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', margin: `6px ${px} 0` , borderRadius: '10px' }}>
          <div style={{ display: 'flex', gap: '5px', flex: 1, flexWrap: 'wrap' }}>
            {sel.map(id => { const c = colls.find(x => (x._id || String(x.id)) === id); if (!c) return null; return (
              <div key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(255,255,255,.12)', padding: '2px 9px', borderRadius: '20px', fontSize: '10px', fontWeight: '500', maxWidth: '140px' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name.substring(0, 18)}{c.name.length > 18 ? '…' : ''}</span>
                <button onClick={() => toggleCard(id)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.6)', cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: '0 0 0 2px' }}>×</button>
              </div>
            ); })}
          </div>
          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
            <button onClick={() => setSel([])} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '7px', background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.2)', color: 'white', fontFamily: 'inherit', fontSize: '11px', cursor: 'pointer' }}>
              <i className="ti ti-x" /> Clear
            </button>
            {!isMobile && <button type="button" onClick={() => { const link = `${window.location.origin}/share/${Date.now()}`; navigator.clipboard.writeText(link).then(() => toast('Share link copied to clipboard!', 'success')); }} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '7px', background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.2)', color: 'white', fontFamily: 'inherit', fontSize: '11px', cursor: 'pointer' }}>
              <i className="ti ti-bolt" style={{ color: '#FCD34D' }} /> Quick Share
            </button>}
            <button type="button" onClick={() => { setSel([]); setTab('playbooks'); }} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 12px', borderRadius: '7px', background: '#C8973A', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>
              <i className="ti ti-book-2" /> {isMobile ? 'Build' : 'Build Playbook'}
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', margin: `8px ${px} 0`, flexShrink: 0 }}>
        {(['collaterals', 'playbooks'] as LibTab[]).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '9px 14px', border: 'none', background: 'none', fontFamily: 'inherit', fontSize: '12px', fontWeight: '600', cursor: 'pointer', color: tab === t ? 'var(--navy)' : 'var(--text3)', borderBottom: tab === t ? '2px solid var(--gold)' : '2px solid transparent', display: 'flex', alignItems: 'center', gap: '5px', textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
            <i className={`ti ${t === 'collaterals' ? 'ti-files' : 'ti-book-2'}`} />
            {t.charAt(0).toUpperCase() + t.slice(1)}
            {t === 'playbooks' && <span style={{ background: '#EDF2F7', color: '#4A5568', fontSize: '10px', fontWeight: '700', borderRadius: '20px', padding: '1px 7px' }}>{playbooks.length}</span>}
          </button>
        ))}
      </div>

      {/* Content area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: `12px ${px}` }}>
        {tab === 'collaterals' && (
          <>
            {/* Filters */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
              <div style={{ display: 'flex', gap: '7px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: '160px' }}>
                  <i className="ti ti-search" style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: 'var(--text3)', pointerEvents: 'none' }} />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search collaterals…" style={{ width: '100%', padding: '7px 10px 7px 28px', borderRadius: '8px', border: '1.5px solid var(--border2)', fontFamily: 'inherit', fontSize: '12px', outline: 'none', background: 'white', boxSizing: 'border-box' }} />
                </div>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...selectStyle }}>
                  <option value="default">Default</option>
                  <option value="name">Name A–Z</option>
                  <option value="read-desc">Most read</option>
                </select>
                {!isMobile && (
                  <select value={senderFilter} onChange={e => setSenderFilter(e.target.value)} style={{ ...selectStyle }}>
                    <option value="">All senders</option>
                    <option value="Rahul Nair">Rahul Nair</option>
                    <option value="Sneha Kapoor">Sneha Kapoor</option>
                    <option value="Arjun Mehta">Arjun Mehta</option>
                  </select>
                )}
              </div>
              <div style={{ display: 'flex', gap: '7px', alignItems: 'center', flexWrap: 'wrap' }}>
                <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ ...selectStyle }}>
                  <option value="all">Type: All</option>
                  <option value="pdf">PDF</option>
                  <option value="ppt">Presentations</option>
                  <option value="video">Videos</option>
                  <option value="url">URLs</option>
                </select>
                <select value={readFilter} onChange={e => setReadFilter(e.target.value)} style={{ ...selectStyle }}>
                  <option value="">Read: All</option>
                  <option value="unread">Unread</option>
                  <option value="partial">Partial</option>
                  <option value="read">Fully read</option>
                </select>
                <span style={{ fontSize: '11px', color: 'var(--text3)' }}>{filtered.length} item{filtered.length !== 1 ? 's' : ''}</span>
                {(search || typeFilter !== 'all' || readFilter || senderFilter) && (
                  <button onClick={() => { setSearch(''); setTypeFilter('all'); setReadFilter(''); setSenderFilter(''); }} style={{ fontSize: '11px', color: 'var(--text3)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Clear</button>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: rp({ mobile: 'repeat(2,1fr)', tablet: 'repeat(3,1fr)', desktop: 'repeat(auto-fill,minmax(160px,1fr))' }), gap: '12px' }}>
              {filtered.map(coll => (
                <CollateralCard key={coll._id || coll.id} coll={coll}
                  selected={sel.includes(coll._id || String(coll.id))}
                  onToggle={toggleCard}
                  onPreview={() => setPreviewModal({ coll, page: 0 })}
                  onAnalytics={() => toast(`Analytics for ${coll.name}`)}
                />
              ))}
            </div>
          </>
        )}
        {tab === 'playbooks' && <PlaybooksPanel />}
      </div>

      {/* Preview modal */}
      {previewModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,27,45,.52)', zIndex: 500, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? '0' : '16px' }} onClick={() => setPreviewModal(null)}>
          <div style={{ background: 'white', borderRadius: isMobile ? '20px 20px 0 0' : '16px', width: '100%', maxWidth: isMobile ? '100%' : '560px', maxHeight: isMobile ? '90vh' : '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.25)' }} onClick={e => e.stopPropagation()}>
            {isMobile && <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: '#CBD5E0', margin: '12px auto 0' }} />}
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{previewModal.coll.name}</div>
                <div style={{ fontSize: '11px', color: 'var(--text3)' }}>{previewModal.coll.size} · {previewModal.coll.pages} page{previewModal.coll.pages !== 1 ? 's' : ''}</div>
              </div>
              <button onClick={() => setPreviewModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: '20px', padding: '2px' }}>×</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>
              {(previewModal.coll.pdata || []).map((p, i) => (
                <div key={i} style={{ marginBottom: '12px', padding: '14px', background: i === previewModal.page ? '#FDF4E7' : 'var(--surface)', borderRadius: '10px', border: i === previewModal.page ? '2px solid #C8973A' : '1.5px solid transparent', cursor: 'pointer' }} onClick={() => setPreviewModal(prev => prev ? { ...prev, page: i } : null)}>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#C8973A', marginBottom: '3px', textTransform: 'uppercase' }}>Page {i+1}</div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--navy)', marginBottom: '2px' }}>{p.t}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text3)', marginBottom: '5px' }}>{p.s}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text2)' }}>{p.b}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {showUpload && currentUser && (
        <UploadModal onClose={() => setShowUpload(false)} currentUser={currentUser} />
      )}
    </div>
  );
}
